import { type SupabaseClient } from '@supabase/supabase-js';
import { Database } from './database.types';
import { supabase as primaryClient } from './supabaseClient';

// Export the existing client directly to ensure we use the same instance everywhere
export const supabase = primaryClient;

// Keep the LazySupabaseClient for backward compatibility but ensure it uses the same URL/key
class LazySupabaseClient {
  private client: SupabaseClient<Database> | null = null;

  // Create a mock client for testing
  private createMockClient(): SupabaseClient<Database> {
    return {
      // Add minimal mock implementations
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        onAuthStateChange: () => ({ 
          data: { subscription: { unsubscribe: () => {} } }
        }),
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: null }),
        signUp: () => Promise.resolve({ data: { user: null, session: null }, error: null }),
        signOut: () => Promise.resolve({ error: null }),
      },
      from: () => ({
        select: () => ({
          eq: () => ({
            then: (callback: any) => callback({ data: [], error: null }),
          }),
        }),
      }),
    } as unknown as SupabaseClient<Database>;
  }

  // Create a real client for production - use the exact same URL/key as supabaseClient.ts
  private createRealClient(): SupabaseClient<Database> {
    // Just return the primary client to ensure consistency
    return primaryClient;
  }

  get instance(): SupabaseClient<Database> {
    // Use the existing client if already created
    if (this.client) {
      return this.client;
    }
    
    // Check for test environment in multiple ways
    const isCypressTest = 
      (typeof window !== 'undefined' && (window as any).isCypressTest === true) ||
      (typeof window !== 'undefined' && (window as any).Cypress) ||
      import.meta.env.MODE === 'test' ||
      import.meta.env.VITE_CYPRESS === 'true';
    
    this.client = isCypressTest 
      ? this.createMockClient() 
      : this.createRealClient();
    
    return this.client;
  }
}